import { useTranslation } from "react-i18next";
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { parseISO, format } from "date-fns";
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import '../styles/MobileModal.css';

import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import { Document, Page, pdfjs } from "react-pdf";
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

function PayslipModal({modalOpen, payslip, downloadPayslip, printPayslip}){
  const {t} = useTranslation();
  const navigate = useNavigate();
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const pdfAreaRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [pdfWidth, setPdfWidth] = useState(0);
  const [pdfHeight, setPdfHeight] = useState(0);

  const [uiVisible, setUiVisible] = useState(true);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [lastScale, setLastScale] = useState(1);
  const [initialFitScale, setInitialFitScale] = useState(1);

  const touchState = useRef({
    lastTap: 0,
    startX: 0,
    startY: 0,
    initialDistance: 0,
    pinchMidpoint: { x: 0, y: 0 },
    panLast: { x: 0, y: 0 },
    moved: false
  });

  // Here for testing. If we'll ever get email contact from payslip data
  const contactEmail = null; // Get from real data
  const emailSubject = encodeURIComponent(`Help with payslip ${payslip ? format(parseISO(payslip.payment_date), "d.M.yyyy") : ''}`);
  const emailBody = encodeURIComponent(`Hello,\n\nI need help with my payslip.\n\nThanks!`); // Translatable

  function onDocumentLoadSuccess({ numPages }){
    setNumPages(numPages); // +1 for testing
    setPageNumber(1);
  }

  const nextPage = () => setPageNumber((prev) => Math.min(prev + 1, numPages));
  const prevPage = () => setPageNumber((prev) => Math.max(prev - 1, 1));

  function onPageRenderSuccess(page){
    setPdfWidth(page.view[2]);
    setPdfHeight(page.view[3]);

    const viewport = page.getViewport({ scale: 1 });
    const fitScale = pdfAreaRef.current.clientWidth / viewport.width;

    setInitialFitScale(fitScale);
    setScale(1);
  }

  useEffect(() =>{
    if(!pdfWidth || !pdfHeight) return;
    
    const maxX = Math.max(0, (pdfWidth * initialFitScale * scale - window.innerWidth) / 2);
    const maxY = Math.max(0, (pdfHeight * initialFitScale * scale - window.innerHeight) / 2);

    setOffset((prev) =>({
      x: Math.min(maxX, Math.max(-maxX, prev.x)),
      y: Math.min(maxY, Math.max(-maxY, prev.y)),
    }));
  },[scale, initialFitScale, pdfWidth, pdfHeight]);

  function getDistance(e){
    const [a, b] = e.touches;
    return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
  }

  function getMidpoint(e){
    const [a, b] = e.touches;
    return {
      x: (a.clientX + b.clientX) / 2,
      y: (a.clientY + b.clientY) / 2
    };
  }

  function clampOffset(offset, scale){
    const viewport = pdfAreaRef.current.getBoundingClientRect();
    const scaledWidth  = pdfWidth  * initialFitScale * scale;
    const scaledHeight = pdfHeight * initialFitScale * scale;

    const maxX = Math.max(0, (scaledWidth  - viewport.width)  / 2);
    const maxY = Math.max(0, (scaledHeight - viewport.height) / 2);

    return {
      x: Math.max(-maxX, Math.min(maxX, offset.x)),
      y: Math.max(-maxY, Math.min(maxY, offset.y))
    };
  }

  function handleTouchStart(e){
    if(e.touches.length === 1){
      const t = e.touches[0];
      touchState.current.startX = t.clientX;
      touchState.current.startY = t.clientY;
      touchState.current.moved = false;

      if(scale > 1){
        touchState.current.panLast = { x: t.clientX, y: t.clientY };
        setUiVisible(false);
      }
    }

    if (e.touches.length === 2) {
      e.preventDefault();

      touchState.current.initialDistance = getDistance(e);
      touchState.current.pinchMidpoint = getMidpoint(e);

      setLastScale(scale);
      touchState.current.moved = true;
      setUiVisible(false);
    }
  }

  function handleTouchMove(e) {
    if (e.touches.length === 2) {
      // PINCH -- Zooming zooms to/from center of pdf
      e.preventDefault();
      touchState.current.moved = true;

      const dist = getDistance(e);
      const ratio = dist / touchState.current.initialDistance;
      let newScale = Math.max(1, Math.min(lastScale * ratio, 4));

      setScale(newScale);
      return;
    }

    if(e.touches.length === 1 && scale > 1){
      // PAN/DRAG
      e.preventDefault();
      touchState.current.moved = true;

      const now = e.touches[0];
      const last = touchState.current.panLast;

      const dx = now.clientX - last.x;
      const dy = now.clientY - last.y;

      touchState.current.panLast = { x: now.clientX, y: now.clientY };

      setOffset(prev => clampOffset({ x: prev.x + dx, y: prev.y + dy }, scale));
    }
  }

  function handleTouchEnd(e) {
    const wasMoved = touchState.current.moved;

    // Swipe to next/prev page when NOT zoomed in
    if(!wasMoved && scale === 1 && e.changedTouches.length === 1){
      const dx = e.changedTouches[0].clientX - touchState.current.startX;

      if(Math.abs(dx) > 60){
        dx < 0 ? nextPage() : prevPage();
        return;
      }
    }

    // TAP (toggle UI)
    if(!wasMoved){
      const now = Date.now();
      // bigger number helps with rämpytys, but still hides after one click right away if visible
      if(now - touchState.current.lastTap < 750) return;
      touchState.current.lastTap = now;
      setUiVisible(prev => !prev);
    }
  }

  return(
    <>
      {modalOpen && (
        <div className="modalPdfViewer">
          <div className={`pdfModalHeader pt-3 ${uiVisible ? "visible" : "hidden"}`}>
            <div className="col-auto me-2">
              <button data-message={t('pdf.modalClose')} onClick={() => navigate(-1)} className="backBtn textCenter">
                <i aria-hidden="true" className="bi bi-arrow-left-short"></i>{/* × */}
              </button>
            </div>
            <div className="col">
              <h4 className="mb-2">{payslip?.organization_name}</h4>
              <h2 className="mb-0">{payslip ? format(parseISO(payslip.payment_date), "d.M.yyyy") : ''}</h2>
            </div>
          </div>
          <div className="pdfContainerMobile" ref={pdfAreaRef} onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
            <div className="pdfMobileZoomLayer" style={{ transform: `translate3d(${offset.x}px, ${offset.y}px, 0) scale(${initialFitScale * scale})` }}>
              <Document className="pdfDocumentMobile" file={`${payslip.file_path}`} onLoadSuccess={onDocumentLoadSuccess}
              noData={t("errorMessages.PDFLoad")} error={t("errorMessages.PDFLoad")} loading={t("pdf.PDFLoading")}>
                <Page onLoadSuccess={onPageRenderSuccess} pageNumber={pageNumber} scale={1} renderAnnotationLayer={false} renderTextLayer={false}
                  error={t("errorMessages.pageLoad")} loading={t("pdf.pageLoading")} noData={t("errorMessages.pageLoad")}/>
              </Document>
            </div>
          </div>
          <div className={`pdfModalFooter align-items-center justify-content-evenly gap-2 ${uiVisible ? "visible" : "hidden"}`}>
            <div className="d-flex align-items-center justify-content-between gap-4 justify-content-sm-around gap-sm-5">
              {/* <button data-message={t('pdf.printPDF')} onClick={printPayslip}><i aria-hidden="true" className="bi bi-printer"></i></button> */}
              {contactEmail && (
                <a href={`mailto:${contactEmail}?subject=${emailSubject}&body=${emailBody}`}>
                  <i aria-hidden="true" className="bi bi-envelope-at"></i><span className="visually-hidden">{t('pdf.sendEmail')}</span>
                </a>
              )}
              <button data-message={t('pdf.downloadPDF')} onClick={downloadPayslip}><i aria-hidden="true" className="bi bi-filetype-pdf"></i></button>
            </div>
            <div className="divider col-auto"/>
            <div className="d-flex align-items-center justify-content-between gap-1">
              <button data-message={t('pdf.pageBack')} onClick={prevPage} disabled={pageNumber <= 1}><i aria-hidden="true" className={`bi bi-caret-left-fill ${pageNumber <= 1 ? "modalPdfNavBtnDisabled" : ''}`}></i></button>
              <p className="mb-0" style={{fontSize:'1.3rem'}}>[ {numPages === 0 ? '0' : pageNumber} / {numPages} ]</p>
              <button data-message={t('pdf.pageForward')} onClick={nextPage} disabled={pageNumber >= numPages }><i aria-hidden="true" className={`bi bi-caret-right-fill ${pageNumber >= numPages ? "modalPdfNavBtnDisabled" : ''}`}></i></button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default PayslipModal;
